import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import NavDrawer from "./NavDrawer";
import SiteLink from "../global/SiteLink";
import { useSite } from "../../site/SiteContext";

// Logo, links (Menu links → Top navigation), company profile and language
// switch all come from the dashboard.
function NavBar() {
    const { site, ui, otherLanguageUrl } = useSite();
    const location = useLocation();
    const [drawer, setDrawer] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    const profile = site.documents?.company_profile;
    const logo = site.brand?.logo;
    // const brandOlive = "#b8975a";
    const accentColor = "#b8975a";

    return (
        <>
            <Box
                component="header"
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    transition: 'all 0.3s',
                    backgroundColor: '#191F2B',
                    boxShadow: scrolled ? '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3)' : 'none',
                    borderBottom: '1px solid #2d3748'
                }}
            >
                <Box sx={{ mx: 'auto', maxWidth: '1280px', px: { xs: 2, sm: 3, lg: 4 }, py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {/* Logo */}
                        <Box
                            component={Link}
                            to="/"
                            sx={{
                                order: { xs: 1, lg: 1 },
                                display: 'flex',
                                minWidth: 0,
                                alignItems: 'center',
                                gap: { xs: '8px', sm: '12px' },
                                transition: 'opacity 0.2s',
                                '&:hover': { opacity: 0.85 },
                                textDecoration: 'none'
                            }}
                            onClick={scrollToTop}
                        >
                            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                {logo && <img src={logo.url} alt={site.brand?.name} style={{ objectFit: 'contain', height: '60px', width: 'auto' }} />}
                            </Box>
                            {/* <Box
                                component="span"
                                sx={{
                                    display: 'inline-flex',
                                    flexShrink: 0,
                                    userSelect: 'none',
                                    alignItems: 'center',
                                    whiteSpace: 'nowrap',
                                    lineHeight: 1,
                                    justifyContent: lng === 'ar' ? 'flex-end' : 'flex-start',
                                    textAlign: lng === 'ar' ? 'right' : 'left',
                                    height: '48px',
                                    fontSize: '20.1px'
                                }}
                            >
                                <span dir={lng === 'ar' ? 'rtl' : 'ltr'} style={{ display: 'inline-block', fontWeight: 'bold', letterSpacing: '0.01em' }}>
                                    <span style={{ color: brandOlive }}>{lng === 'ar' ? 'ركن درفة' : 'Rukn Drfah'}</span>{' '}

                                </span>
                            </Box> */}
                        </Box>

                        {/* Navigation Links */}
                        <Box component="nav" sx={{ order: 3, display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                            {site.menus?.header?.map((page, i) => {
                                const pagePath = page.url;
                                const isActive = !page.external && (location.pathname === pagePath || (pagePath === '/' && location.pathname === ''));

                                return (
                                    <Box
                                        key={i}
                                        component={SiteLink}
                                        to={pagePath}
                                        newTab={page.new_tab}
                                        onClick={scrollToTop}
                                        sx={{
                                            px: 2,
                                            py: 1,
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                            transition: 'all 0.2s',
                                            position: 'relative',
                                            borderRadius: '0.5rem',
                                            textDecoration: 'none',
                                            ...(isActive ? {
                                                color: accentColor,
                                                backgroundColor: 'rgba(214, 188, 88, 0.05)'
                                            } : {
                                                color: '#F3F4F6',
                                                '&:hover': {
                                                    color: '#fff',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                                }
                                            })
                                        }}
                                    >
                                        {page.label}
                                        {isActive && (
                                            <Box component="span" sx={{
                                                position: 'absolute',
                                                bottom: '4px',
                                                left: '16px',
                                                right: '16px',
                                                height: '4px',
                                                backgroundColor: accentColor,
                                                borderRadius: '9999px'
                                            }} />
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Actions */}
                        <Box sx={{ order: 4, display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1.5 }}>
                            {profile?.url && <a href={profile.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }} {...(profile.type === 'pdf' && { download: true })}>
                                <Box component="span" sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    borderRadius: '0.75rem',
                                    border: `1px solid rgba(214, 188, 88, 0.2)`,
                                    backgroundColor: 'rgba(214, 188, 88, 0.1)',
                                    px: 2,
                                    py: 1.25,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: '#F3F4F6',
                                    transition: 'all 0.2s',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: accentColor,
                                        color: '#fff'
                                    }
                                }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 15V3"></path><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><path d="m7 10 5 5 5-5"></path></svg>
                                    {site.layout?.navbar?.profile_label}
                                </Box>
                            </a>}
                            <Box
                                component="a"
                                href={otherLanguageUrl()}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    px: 1.5,
                                    py: 0.75,
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: 'rgba(243, 244, 246, 0.8)',
                                    border: '1px solid #4B5563',
                                    borderRadius: '0.375rem',
                                    transition: 'all 0.2s',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: '#fff',
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                    }
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path><path d="M2 12h20"></path></svg>
                                {ui('common.switch_language')}
                            </Box>
                        </Box>

                        {/* Mobile Menu Button */}
                        <Box
                            component="button"
                            onClick={() => setDrawer(true)}
                            aria-label="Open menu"
                            sx={{
                                order: 2,
                                display: { xs: 'inline-flex', lg: 'none' },
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '48px',
                                width: '48px',
                                borderRadius: '1rem',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                color: '#F3F4F6',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
                                backdropFilter: 'blur(4px)',
                                transition: 'all 0.2s',
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)'
                                }
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 5h16"></path><path d="M4 12h16"></path><path d="M4 19h16"></path></svg>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <NavDrawer drawer={drawer} setDrawer={setDrawer} />
        </>
    );
}

export default NavBar;
